/**
 * 透视裁剪的应用层接线（M4-02 / DESIGN.md §6.5）。
 *
 * 用户拖出任意四边形（比如照片里一张倾斜的白板/文档），映射回一个
 * 轴对齐矩形——输出尺寸取四边形对边的平均长度，这样文字/线条的疏密
 * 大致保持原样，不会被拉伸变形。
 */
import { compositeDocument } from './composite';
import { createSetCanvasSizeCommand } from './commands/canvas-commands';
import { samplePerspective, solveHomography, type Point } from '../engine/transform/perspective';
import { TILE, tileKey } from '../engine/tile/key';
import { BYTES_PER_TILE } from '../engine/tile/store';
import type { AppStore } from './store';

function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** 四边形（顺序：左上、右上、右下、左下）→ 输出矩形尺寸（对边平均长）。 */
function outputSizeOf(corners: readonly [Point, Point, Point, Point]): {
  width: number;
  height: number;
} {
  const [tl, tr, br, bl] = corners;
  const width = Math.round((distance(tl, tr) + distance(bl, br)) / 2);
  const height = Math.round((distance(tl, bl) + distance(tr, br)) / 2);
  return { width: Math.max(1, width), height: Math.max(1, height) };
}

/**
 * 提交透视裁剪：四边形 corners（文档空间，左上/右上/右下/左下顺序）→
 * 矩形输出，替换活动 raster 图层内容并把画布改成输出尺寸。
 * 一条历史（画布尺寸 + 像素）。返回是否执行。
 */
export function perspectiveCropDocument(
  store: AppStore,
  corners: readonly [Point, Point, Point, Point],
): boolean {
  const document = store.document;
  if (document === null) return false;
  const target = store.activeRasterLayerId();
  if (target === null) return false;

  const { width, height } = outputSizeOf(corners);
  const rectCorners: readonly [Point, Point, Point, Point] = [
    { x: 0, y: 0 },
    { x: width, y: 0 },
    { x: width, y: height },
    { x: 0, y: height },
  ];
  // 正向：矩形输出坐标 → 源图四边形坐标（用于逆采样：每个输出像素反查源坐标）
  const homography = solveHomography(rectCorners, corners);

  const flat = compositeDocument(document, store.tiles);
  const sampled = samplePerspective(flat.pixels, flat.width, flat.height, width, height, homography);

  const transaction = store.beginPixelTransaction('透视裁剪');
  const tx1 = Math.ceil(width / TILE) - 1;
  const ty1 = Math.ceil(height / TILE) - 1;
  const keys = [];
  for (let ty = 0; ty <= ty1; ty++) {
    for (let tx = 0; tx <= tx1; tx++) {
      const key = tileKey(target, tx, ty);
      transaction.touch(key);
      keys.push({ tx, ty, key });
    }
  }
  transaction.capture();

  const dirty: Array<{ tx: number; ty: number }> = [];
  for (const { tx, ty, key } of keys) {
    const out = new Uint8Array(BYTES_PER_TILE);
    const originX = tx * TILE;
    const originY = ty * TILE;
    let any = false;
    for (let y = 0; y < TILE; y++) {
      const docY = originY + y;
      if (docY >= height) break;
      for (let x = 0; x < TILE; x++) {
        const docX = originX + x;
        if (docX >= width) break;
        const from = (docY * width + docX) * 4;
        if (sampled[from + 3] === 0) continue;
        const to = (y * TILE + x) * 4;
        out[to] = sampled[from]!;
        out[to + 1] = sampled[from + 1]!;
        out[to + 2] = sampled[from + 2]!;
        out[to + 3] = sampled[from + 3]!;
        any = true;
      }
    }
    if (any) {
      store.tiles.set(key, out);
      dirty.push({ tx, ty });
    }
  }

  store.setSelection(null);
  store.dispatchWithPixels(createSetCanvasSizeCommand(width, height), transaction.end());
  store.invalidatePixels(dirty);
  return true;
}
